import { FC, PropsWithChildren, useState } from 'react'

import { Link, useParams } from '@remix-run/react'

import {
  Container,
  PageHeading,
  PrimaryButton,
  SectionHeading,
  SeeMoreButton,
  SubSectionHeading,
} from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { ContactCtaSection } from '~/features'
import { OfficeCard } from '~/features/plan-detail-page/components'
import {
  availabilityToJaText,
  contractTypeToJaText,
} from '~/features/plan-detail-page/helpers/convert-to-text'
import { twMerge } from '~/lib'
import { SpaceIncludingOffice } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

type PlanDetailPageProps = {
  plan: SpaceIncludingOffice
}

const InnerContainer: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="container mx-auto lg:max-w-none lg:px-6">{children}</div>
  )
}

const OuterContainer: FC<PropsWithChildren> = ({ children }) => {
  return <div className="mx-auto min-[844px]:max-w-[844px]">{children}</div>
}

export const PlanDetailPage: FC<PlanDetailPageProps> = ({ plan }) => {
  const { prefectureSlug, officeId, planId } = useParams<
    'prefectureSlug' | 'officeId' | 'planId'
  >()
  const { availability } = plan
  const isAvailableOrSoon =
    availability === 'AVAILABLE' || availability === 'AVAILABLE_SOON'
  const [isDescriptionOmitted, setIsDescriptionOmitted] = useState(
    (plan.description?.length ?? 0) > 100,
  )
  const planDescriptionLength = plan.description?.length ?? 0

  const sortedImages = plan.images.sort((a, b) => a.priority - b.priority)
  const firstImage =
    sortedImages.find((image) => image.isEyeCatch) || sortedImages[0]
  plan.images.find((image) => image.isEyeCatch) || plan.images[0]

  return (
    <main className="pb-20 pt-6 md:pt-10">
      <meta content={plan.name} property="og:title" />
      <meta content={plan.description || ''} property="og:description" />
      <meta content="product" property="og:type" />
      <meta
        content={`/prefectures/${prefectureSlug}/offices/${officeId}/plans/${planId}`}
        property="og:url"
      />
      <meta
        content={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
        property="og:image"
      />
      <div className="fixed bottom-0 z-20 w-full border-t border-grayBorder bg-white p-2.5 text-center">
        <Container>
          <Link
            rel="noreferrer"
            target="_blank"
            to={`/prefectures/${prefectureSlug}/offices/${officeId}/plans/${planId}/contact`}
          >
            <PrimaryButton
              className="w-full max-w-[454px]"
              color="cta"
              outlined={!isAvailableOrSoon}
            >
              {isAvailableOrSoon
                ? 'お問い合わせ・内見予約'
                : '空き待ち予約する'}
            </PrimaryButton>
          </Link>
        </Container>
      </div>
      <OuterContainer>
        <PageHeading className="mb-4 px-4 md:mb-6 md:px-0">
          プラン詳細
        </PageHeading>
        <div className="mb-6 md:mb-16">
          <div className="mb-6">
            <div
              className={twMerge(
                'flex justify-between p-2 font-bold leading-[18px] text-white md:p-4',
                isAvailableOrSoon ? 'bg-brand' : 'bg-grayText',
              )}
            >
              <div className="flex justify-between gap-x-1">
                {plan.type.map((t) => (
                  <span
                    className="flex min-w-24 items-center justify-center border border-white px-2 text-xs md:text-sm"
                    key={t.name}
                  >
                    {t.name}
                  </span>
                ))}
              </div>
              <span className="text-[13px] md:text-base">
                {availabilityToJaText(availability)}
              </span>
            </div>
            <div className="w-full">
              <img
                alt={`${plan.name}の写真`}
                className="h-full w-full object-cover"
                src={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
              />
            </div>
          </div>
          <div className="mb-6">
            <InnerContainer>
              <PageHeading className="mb-4">{plan.name}</PageHeading>
              <p className={twMerge('whitespace-pre-line text-body', 'mb-2')}>
                {isDescriptionOmitted ? (
                  <p>{plan.description?.slice(0, 100) + '...'}</p>
                ) : (
                  <>
                    <p>{plan.description}</p>
                    {planDescriptionLength > 100 && (
                      <SeeMoreButton
                        className="mx-auto mt-4 md:mx-0"
                        onClick={() => setIsDescriptionOmitted(true)}
                        title="閉じる"
                      />
                    )}
                  </>
                )}
              </p>
              {isDescriptionOmitted && (
                <SeeMoreButton
                  className="mx-auto md:mx-0"
                  onClick={() => setIsDescriptionOmitted(false)}
                  title="もっと見る"
                />
              )}
            </InnerContainer>
          </div>
          <section className="bg-brand-bg-light py-6">
            <InnerContainer>
              <SectionHeading className="mb-4" color="brand">
                賃料
              </SectionHeading>
              <div className="space-y-4">
                {plan.monthlyCostExcludingTax === null ||
                plan.monthlyCostIncludingTax === null ||
                plan.isMonthlyCostPrivate ? (
                  <SubSectionHeading className="mb-1">
                    月額費用：お問い合わせ
                  </SubSectionHeading>
                ) : (
                  <div>
                    <SubSectionHeading className="mb-1">
                      <span>
                        月額費用：
                        {plan.monthlyCostExcludingTax.toLocaleString()}円
                      </span>
                      <span className="text-body-s font-normal">
                        （税込{plan.monthlyCostIncludingTax.toLocaleString()}
                        円）
                      </span>
                    </SubSectionHeading>
                    <p className="whitespace-pre-line text-sm leading-[160%] text-grayText">
                      {plan.monthlyCostRemarks}
                    </p>
                  </div>
                )}
                {plan.initialCostExcludingTax === null ||
                plan.initialCostIncludingTax === null ||
                plan.isInitialCostPrivate ? (
                  <SubSectionHeading className="mb-1">
                    初期費用総額：お問い合わせ
                  </SubSectionHeading>
                ) : (
                  <div>
                    <SubSectionHeading className="mb-1">
                      <span>
                        初期費用総額：
                        {plan.initialCostExcludingTax.toLocaleString()}円
                      </span>
                      <span className="text-body-s font-normal">
                        （税込{plan.initialCostIncludingTax.toLocaleString()}
                        円）
                      </span>
                    </SubSectionHeading>
                    <p className="whitespace-pre-line text-sm leading-[160%] text-grayText">
                      {plan.initialCostRemarks}
                    </p>
                  </div>
                )}
              </div>
            </InnerContainer>
          </section>
          <section className="py-6">
            <InnerContainer>
              <SectionHeading className="mb-4" color="brand">
                契約
              </SectionHeading>
              <div className="space-y-4">
                <div>
                  <SubSectionHeading className="mb-1">
                    契約種別
                  </SubSectionHeading>
                  <p className="text-body">
                    {contractTypeToJaText(plan.contractType)}
                  </p>
                </div>
                <div>
                  <SubSectionHeading className="mb-1">
                    契約期間
                  </SubSectionHeading>
                  <p className="text-body">
                    {plan.contractPeriod && plan.contractPeriod > 0
                      ? `${plan.contractPeriod}ヶ月`
                      : 'お問い合わせ'}
                  </p>
                </div>
                <div>
                  <SubSectionHeading className="mb-1">
                    入居可能時期
                  </SubSectionHeading>
                  <p className="text-body">{plan.availableMoveInDate || '-'}</p>
                </div>
                <div>
                  <SubSectionHeading className="mb-1">
                    期間内解約
                  </SubSectionHeading>
                  <p className="text-body">{plan.cancellation || '-'}</p>
                </div>
              </div>
            </InnerContainer>
          </section>
          <section className="bg-brand-bg-light py-6">
            <InnerContainer>
              <SectionHeading className="mb-4" color="brand">
                施設情報
              </SectionHeading>
              <div className="space-y-4">
                <div>
                  <SubSectionHeading className="mb-1">階数</SubSectionHeading>
                  <p className="text-body">
                    {plan.floor && plan.floor !== 0 ? `${plan.floor}階` : '-'}
                  </p>
                </div>
                <div>
                  <SubSectionHeading className="mb-1">広さ</SubSectionHeading>
                  <p className="text-body">
                    {plan.size && plan.size !== 0 ? `${plan.size}㎡` : '-'}
                  </p>
                </div>
                <div>
                  <SubSectionHeading className="mb-1">
                    利用人数
                  </SubSectionHeading>
                  <p className="text-body">
                    {plan.maxPeople !== null ? `〜${plan.maxPeople}名` : '-'}
                  </p>
                </div>
              </div>
            </InnerContainer>
          </section>
          <section className="py-6">
            <InnerContainer>
              <SectionHeading className="mb-4" color="brand">
                備考
              </SectionHeading>
              <p className="whitespace-pre-line text-body">
                {plan.remarks || '-'}
              </p>
            </InnerContainer>
          </section>
          <section className="bg-brand-bg-light py-6">
            <InnerContainer>
              <SectionHeading className="mb-4" color="brand">
                写真
              </SectionHeading>
              <div className="flex flex-wrap gap-4">
                {plan.images.length === 0 && (
                  <p className="text-body">
                    このプランの写真は見つかりませんでした。
                  </p>
                )}
                {plan.images.map((image) => (
                  <div
                    className="h-[228px] basis-full md:basis-[calc((100%-16px)/2)]"
                    key={image.id}
                  >
                    <img
                      alt={`${plan.name}の写真`}
                      className="h-full w-full object-cover"
                      src={encodeImageUrl(image.url)}
                    />
                  </div>
                ))}
              </div>
            </InnerContainer>
          </section>
        </div>
        <ContactCtaSection
          className="mb-6 md:mb-16"
          href={`/prefectures/${prefectureSlug}/offices/${officeId}/plans/${planId}/contact`}
          isAvailable={isAvailableOrSoon}
        >
          {isAvailableOrSoon ? (
            <>
              <p>さらに詳細を知りたい方、</p>
              <p>実際にオフィスを見学してみたい方はこちら</p>
            </>
          ) : (
            <p>空きが出た場合、ご連絡します</p>
          )}
        </ContactCtaSection>
        <InnerContainer>
          <h2 className="mb-2 text-body">オフィス詳細</h2>
          <OfficeCard office={plan.office} />
        </InnerContainer>
      </OuterContainer>
    </main>
  )
}
